import { createSlice, createAction } from "@reduxjs/toolkit";

// Create the actions
export const setConfirmedOverride = createAction(
  "invoices/setConfirmedOverride"
);
export const resetConfirmedOverridesChecked = createAction(
  "invoices/resetConfirmedOverridesChecked"
);

const initialState = {
  invoices: [],
  isLoading: false,
  error: null,
  invoicesInProgress: [],
  confirmedOverrides: {},
  invoicesNeedsReview: [],
};

export const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    fetchInvoicesStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchInvoicesSuccess: (state, action) => {
      state.invoices = action.payload;
      state.isLoading = false;
      state.error = null;
      
      // Clean up confirmedOverrides by removing invoices that are no longer present
      if (Object.keys(state.confirmedOverrides).length > 0) {
        const currentInvoiceIds = new Set(action.payload.map(invoice => invoice.invoice_id));
        const overrideInvoiceIds = Object.keys(state.confirmedOverrides);
        
        // Find invoices in overrides that are no longer in the current invoices list
        const invoicesToRemove = overrideInvoiceIds.filter(id => !currentInvoiceIds.has(id));
        
        // Remove those invoices from confirmedOverrides
        invoicesToRemove.forEach(id => {
          delete state.confirmedOverrides[id];
        });
      }
    },
    fetchInvoicesError: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    updateInvoicesAddedToCart: (state) => {
      state.invoices = state.invoices.map((invoice) => {
        if (state.invoicesInProgress.includes(invoice.invoice_id)) {
          return { ...invoice, added_to_cart: true };
        }
        return invoice;
      });
    },
    updateInvoicesInProgress: (state, action) => {
      state.invoicesInProgress = action.payload;
    },
    updateInvoicesNeedsReview: (state, action) => {
      state.invoices = state.invoices.map((invoice) => {
        if (action.payload.includes(invoice.invoice_id)) {
          return { ...invoice, failed_to_add_all_items: true };
        }
        return invoice;
      });
    },
    setStyleMismatchAcknowledged: (state, action) => {
      const { invoiceId, lineItemId, acknowledged } = action.payload;
      
      // Find the invoice in the invoices array
      const invoiceIndex = state.invoices.findIndex(inv => inv.invoice_id === invoiceId);
      if (invoiceIndex !== -1) {
        // Find the item in the dgi_items array
        const items = state.invoices[invoiceIndex].dgi_items || [];
        const itemIndex = items.findIndex(item => item.line_item_id === lineItemId);
        
        if (itemIndex !== -1) {
          // Get the base group ID for this item
          const clickedItem = items[itemIndex];
          const baseGroupId = lineItemId.split("-")[0];
          
          // Update the acknowledgment flag for ALL items in the same group
          items.forEach((item, idx) => {
            // Check if this item belongs to the same group (has same base ID)
            const itemBaseId = item.line_item_id?.split("-")[0];
            if (itemBaseId === baseGroupId) {
              // Update this item's acknowledgment flag
              state.invoices[invoiceIndex].dgi_items[idx].style_mismatch_acknowledged = acknowledged;
              
              // If the item has style_mismatch_warning flag, make sure it's consistent too
              if (clickedItem.style_mismatch_warning !== undefined) {
                state.invoices[invoiceIndex].dgi_items[idx].style_mismatch_warning = 
                  clickedItem.style_mismatch_warning;
              }
            }
          });
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setConfirmedOverride, (state, action) => {
        const { invoiceId, lineItemId, style, color, quantity, sizes, style_mismatch_acknowledged } =
          action.payload;

        // Extract just the base ID without style/color/size information
        const baseLineItemId = lineItemId.split("-")[0];

        if (!state.confirmedOverrides[invoiceId]) {
          state.confirmedOverrides[invoiceId] = {};
        }

        // Get the master size list from the style
        const masterSizeList = style?.master_size_list || [];

        // Convert any simple number values to object format and organize by master size list
        const formattedSizes = {};

        // First process sizes that are in the master size list, in order
        if (sizes && masterSizeList.length > 0) {
          // Add sizes in the order they appear in the master size list
          masterSizeList.forEach((size) => {
            if (size in sizes) {
              const value = sizes[size];
              formattedSizes[size] =
                typeof value === "number"
                  ? { quantity: value, checked: true }
                  : value;
            }
          });

          // Add any remaining sizes that weren't in the master size list
          Object.entries(sizes).forEach(([size, value]) => {
            if (!masterSizeList.includes(size)) {
              formattedSizes[size] =
                typeof value === "number"
                  ? { quantity: value, checked: true }
                  : value;
            }
          });
        } else {
          // If no master size list, just convert the values
          if (sizes) {
            Object.entries(sizes).forEach(([size, value]) => {
              formattedSizes[size] =
                typeof value === "number"
                  ? { quantity: value, checked: true }
                  : value;
            });
          }
        }

        state.confirmedOverrides[invoiceId][baseLineItemId] = {
          dgi_style_sku: style?.dgi_style_sku,
          master_style: style?.master_style,
          master_short_description: style?.master_short_description,
          master_brand: style?.master_brand,
          master_size_list: masterSizeList, // Store the master size list for future reference
          confirmedColor: color,
          quantity,
          sizes: formattedSizes || {}, // Store size overrides with the new structure
          style_mismatch_acknowledged: style_mismatch_acknowledged
        };
      })
      .addCase(resetConfirmedOverridesChecked, (state) => {
        // Deep clone the current overrides
        const updatedOverrides = JSON.parse(
          JSON.stringify(state.confirmedOverrides)
        );

        // Iterate through all invoices
        Object.keys(updatedOverrides).forEach((invoiceId) => {
          // Iterate through all line items in this invoice
          Object.keys(updatedOverrides[invoiceId]).forEach((lineItemId) => {
            const lineItem = updatedOverrides[invoiceId][lineItemId];

            // If this line item has sizes with checked properties
            if (lineItem.sizes) {
              // Iterate through all sizes and set checked to false
              Object.keys(lineItem.sizes).forEach((size) => {
                if (
                  typeof lineItem.sizes[size] === "object" &&
                  lineItem.sizes[size] !== null
                ) {
                  lineItem.sizes[size].checked = false;
                }
              });
            }
          });
        });

        // Update the state with the modified overrides
        state.confirmedOverrides = updatedOverrides;
      });
  },
});

export const {
  fetchInvoicesStart,
  fetchInvoicesSuccess,
  fetchInvoicesError,
  updateInvoicesAddedToCart,
  updateInvoicesInProgress,
  updateInvoicesNeedsReview,
  setStyleMismatchAcknowledged,
} = invoicesSlice.actions;

// Selectors
export const selectInvoices = (state) => state.invoices.invoices;
export const selectInvoicesLoading = (state) => state.invoices.isLoading;
export const selectInvoicesError = (state) => state.invoices.error;
export const selectConfirmedOverrides = (state, invoiceId) =>
  state.invoices.confirmedOverrides[invoiceId];
export const selectInvoicesNeedsReview = (state) =>
  state.invoices.invoicesNeedsReview;

export default invoicesSlice.reducer;
